import React, { useContext, useState, useEffect, useRef } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import Snackbar from '@material-ui/core/Snackbar';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import { Interweave } from 'interweave';
import ContextProvider from '../../../context';
import CircularProgress from '../../../components/CircularProgress';
import * as courseReviewActions from '../../../actions/courseReview';
import SkillEvaluationForm from '../SkillEvaluationModal/SkillEvaluationForm';

import messages from './messages';
import s from './CourseReviewForm.scss';

const MAX_FEEDBACK_LENGTH = 2000;
const DEFAULT_RATE = 5;

const CourseReviewForm = ({
  intl: { formatMessage },
  isCongratsModal,
  handleClose,
  getCourseReview,
  loading,
  lesson,
  review,
  course,
}) => {
  const { fetch } = useContext(ContextProvider);
  const initialRate = useRef(DEFAULT_RATE);
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [feedback, setFeedback] = useState('');
  const [showSnackbarMessage, setShowSnackbarMessage] = useState(false);
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skillLevel, setSkillLevel] = useState(1);
  const maxLengthErrorText = formatMessage(messages.maxLength, {
    maxCharsQnt: MAX_FEEDBACK_LENGTH,
  });

  const marks = [
    {
      value: 1,
      label: formatMessage(messages.notLikelyLabel),
    },
    {
      value: 5,
      label: formatMessage(messages.likelyLabel),
    },
  ];

  useEffect(() => {
    setRate(_.get(review, 'review.rate', DEFAULT_RATE));
    initialRate.current = _.get(review, 'review.rate', DEFAULT_RATE);
    setFeedback(_.get(review, 'review.feedback', ''));
    // quick fix set the default skill level of the end skill to Weakest
    if (!isCongratsModal) {
      setSkillLevel(_.get(review, 'userSkill.skillLevel', 1));
    }
  }, []);

  const handleFeedbackChange = value => {
    setFeedback(value);
    setError(value && value.toString().trim().length > MAX_FEEDBACK_LENGTH);
  };

  const handleSave = () => {
    setIsSubmitting(true);
    const completedPartsCount =
      _.get(course, 'completedLessons') + _.get(course, 'completedQuizzes');
    const coursePartsTotal =
      _.get(course, 'totalLessons') + _.get(course, 'totalQuizzes');
    const data = {
      review: { rate, feedback: feedback?.trim() },
      completedPartsCount,
      coursePartsTotal,
      skillLevel,
    };
    if (!isCongratsModal) {
      data.lessonId = lesson._id;
    }

    return fetch(`/api/course_reviews/${course._id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
      .then(async res => {
        if (!res.ok) {
          const resJson = await res.json();
          throw new Error(resJson && resJson.message);
        }
        setShowSnackbarMessage(formatMessage(messages.leaveRatingSuccess));
        setIsSubmitting(false);
        setTimeout(() => {
          handleClose();
          getCourseReview(course._id);
        }, 1000);
      })
      .catch(err => {
        setIsSubmitting(false);
        setShowSnackbarMessage(err.message);
      });
  };

  if (loading) {
    return (
      <div className={s.loadingWrapper}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {!isCongratsModal && (
        <Typography variant="h5" color="primary" gutterBottom>
          {initialRate.current ? (
            <FormattedMessage {...messages.currentCourseRatingTitle} />
          ) : (
            <FormattedMessage {...messages.rateCourseTitle} />
          )}
        </Typography>
      )}
      <Typography variant="subtitle1" color="primary" gutterBottom>
        <FormattedMessage {...messages.courseRatingLabel} />
      </Typography>
      <Slider
        min={1}
        max={5}
        value={rate || DEFAULT_RATE}
        marks={marks}
        disabled={isSubmitting}
        classes={{ root: s.sliderRoot, markLabel: s.sliderMarkLabel }}
        onChange={(e, value) => setRate(value)}
      />
      <TextField
        multiline
        fullWidth
        variant="outlined"
        type="text"
        name="feedback"
        margin="normal"
        rows={4}
        error={error || rate <= 3}
        value={feedback}
        onChange={e => handleFeedbackChange(e.target.value)}
        label={formatMessage(messages.courseRatingFeedback)}
        disabled={isSubmitting}
        helperText={error && maxLengthErrorText}
      />
      <SkillEvaluationForm
        setSkillLevel={setSkillLevel}
        skillLevel={skillLevel}
        classes={{
          title: classNames(s.skillEvaluationFormTitle, {
            [s.leftTextAlign]: !isCongratsModal,
          }),
          sliderRoot: s.sliderRoot,
          sliderMarkLabel: s.skillEvaluationSliderMarkLabel,
          skillEvaluationContentWrapper: s.skillEvaluationContentWrapper,
          mark: s.skillEvaluationSliderMark,
        }}
      />
      <div className={s.actions}>
        <Button
          data-test-id="vXuL9t8ekbVIfEm96eng6"
          fullWidth
          color="secondary"
          variant="outlined"
          data-test="skip-course-review-btn"
          disabled={isSubmitting}
          onClick={handleClose}
        >
          {isCongratsModal ? (
            <FormattedMessage {...messages.skipLabel} />
          ) : (
            <FormattedMessage {...messages.closeLabel} />
          )}
        </Button>
        <Button
          data-test-id="Xw6Bff-3of0gS18tdcHge"
          fullWidth
          color="primary"
          variant="outlined"
          data-test="save-course-review-btn"
          disabled={isSubmitting || error}
          onClick={handleSave}
        >
          <FormattedMessage {...messages.saveAndContinueLabel} />
        </Button>
      </div>
      <Snackbar
        open={!!showSnackbarMessage}
        autoHideDuration={2000}
        onClose={() => setShowSnackbarMessage(false)}
        message={<Interweave content={showSnackbarMessage} />}
      />
    </>
  );
};

CourseReviewForm.propTypes = {
  intl: intlShape.isRequired,
  isCongratsModal: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  getCourseReview: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  review: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(null)])
    .isRequired,
  lesson: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  course: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
};

CourseReviewForm.defaultProps = {
  isCongratsModal: false,
};

export default compose(
  injectIntl,
  withStyles(s),
  connect(
    state => ({
      review: state.courseReview.review,
      loading: state.courseReview.loading,
      lesson: state.lessonDetails.lesson,
      course: state.courseDetails.course,
    }),
    dispatch =>
      bindActionCreators(
        {
          ...courseReviewActions,
        },
        dispatch,
      ),
  ),
)(CourseReviewForm);
